const _ = require("lodash");
const {getChapterPathImage} = require("./AssetsUtil");
const ErrorUtil = require("../../../shared/ErrorUtil");
const OpsPage = require("../vault/OpsPage");
const TrainerCore = require("../../../shared/TrainerCore");

const getAdjustedAssetWidths = containerWidth => {
	/**
	 * Essentially a hard-coded list of how much width each asset should take up
	 * inside the visible space of the trainer for various asset types
	 */
	const assetRatios = {
		// landscape
		landscapeEntry: 1 / 2,
		landscapeSegment: 1 / 4,
		landscapeFiller: 1 / 8,
		landscapeExit: 1 / 2,
		// hall
		hallEntry: 1 / 2, // downstairs only
		hallLeft: 1 / 4, // upstairs only
		hallRight: 1 / 8, // upstairs only
		hallSegment: 1 / 4,
		hallFiller: 1 / 8,
		// building elements
		lessonRoom: 1 / 4,
		testRoom: 1 / 2,
		fauxRoom: 1 / 2,
		staircase: 1 / 4,
		roofline: 1 / 4,
		// dotted path
		dottedpathStart: 1 / 4,
		dottedpathEnd: 1 / 4,
		dottedpathStaircase: 1 / 4,
		dottedpathSegment: 1 / 8,
	};
	const assetWidths = {};
	_.each(assetRatios, (val, key) => {
		/**
		 * TL;DR: drop down to the nearest integer (decrements if the value was already an
		 * integer). This is done in an attempt to fix the occasional seams that show up.
		 */
		assetWidths[key] = _.ceil(val * containerWidth) - 1;
	});

	return assetWidths;
};

/**
 * @param {String} type landscape | mainhall
 * @param {Object} opts
 * @returns {Object|String}
 */
const getBackgroundAssets = (type, opts = {}) => {
	const {isUpstairs, isLocked, grade, demo} = opts;
	const floor = getFloorStr(isUpstairs);
	const cp = (location, part) => {
		return getChapterPathImage(
			location,
			part + (isLocked ? "_locked" : ""),
			grade,
			demo
		);
	};
	const assetFilepaths = {
		landscape: {
			segments: [cp("landscape", "repeat_001"), cp("landscape", "repeat_002")],
			entry: cp("landscape", "entry"),
			exit: cp("landscape", "exit"),
			filler: cp("landscape", "filler"),
		},
		mainhall: {
			segments: [
				cp(floor, "mainhall_repeat_001"),
				cp(floor, "mainhall_repeat_002"),
			],
			entry: cp(floor, "mainhall_entry"),
			left: cp(floor, "mainhall_left"),
			right: cp(floor, "mainhall_right"),
			filler: cp(floor, "mainhall_filler"),
			invisible: cp(floor, "mainhall_invisible"),
		},
		upstairs: {
			roofline: cp("upstairs", "roofline"),
		},
	};
	return _.get(assetFilepaths, type);
};

/**
 * @param {Boolean} isLocked
 * @param {String|Integer} grade
 * @param {Object} opts {styleType, cardinal, isReading, isTest, isUpstairs, demo}
 * @returns {String}
 */
const getRoomAsset = (isLocked, grade, opts = {}) => {
	const {
		styleType = "kraken",
		cardinal = "",
		isComplete = false,
		isReading = false,
		isTest = false,
		isUpstairs = false,
		demo = false,
	} = opts;
	const level = getFloorStr(isUpstairs);
	const lockedStr = isLocked ? "locked" : "unlocked";
	const pieces = [];
	if (isTest) {
		pieces.push("test", lockedStr);
		if (!isLocked) {
			const completeStr = isComplete ? "complete" : "incomplete";
			pieces.push(completeStr);
		}
	} else {
		const style = isReading ? "reading" : styleType;
		pieces.push("room", style, cardinal, lockedStr);
	}
	const imgName = pieces.join("_");
	return getChapterPathImage(level, imgName, grade, demo);
};

/**
 * @param {String} lockedStr
 * @param {String} cardinal north | south
 * @param {Boolean} isUpstairs
 * @param {String|Integer} grade
 * @param {Boolean} demo
 * @returns {String}
 */
const getStaircaseAsset = (lockedStr, cardinal, isUpstairs, grade, demo) => {
	const level = getFloorStr(isUpstairs);
	const imgName = `stairs_${cardinal}_${lockedStr}`;
	return getChapterPathImage(level, imgName, grade, demo);
};

/**
 * @param {Boolean} isLocked
 * @param {String} cardinal north | south
 * @param {String|Integer} grade
 * @param {Object} optional
 * @returns
 */
const getDottedPathAsset = (isLocked, cardinal, grade, optional = {}) => {
	const {
		demo = false,
		isComplete = false,
		isEnd = false,
		isForStaircase = false,
		isStart = false,
		isTest = false,
		isUpstairs = false,
		roomIsUnlocked = false,
		useShortPath = false, // only used if isForStaircase == true
	} = optional;
	const level = getFloorStr(isUpstairs);
	const lockedStr = isLocked && !demo ? "locked" : "unlocked";
	let type = "curve";
	let completeStr = "";
	if (isStart) {
		type = "start";
		cardinal = ""; // path start is uniform
	} else if (isEnd) {
		type = "end";
	} else if (isTest) {
		type = "test";
		if (!isLocked) {
			if (roomIsUnlocked) {
				completeStr = isComplete ? "complete" : "incomplete";
			}
		}
	} else if (isForStaircase) {
		type = "stairs";
		if (useShortPath) {
			type += "_short";
		}
	}
	const parts = ["dottedpath", type, cardinal, lockedStr, completeStr];
	const imgName = parts.filter(Boolean).join("_");
	return getChapterPathImage(level, imgName, grade, demo);
};

const blockIsFaux = b => b.blockType === "faux";

const blockIsStaircase = b => _.isArray(b);

const blockIsUpstairs = b => TrainerCore.isBonusBlock(b);

// should return true for core blocks/readings and tests
const blockIsDownstairs = b => !blockIsUpstairs(b);

/**
 * Because there are so many layers of assets on the path (e.g. landscape,
 * hall, etc.), as well as assets that overlap each other on the same layer
 * (e.g. staircases and rooms), their CSS left value (which can be thought
 * of as the x-attribute in canvas) is also outputted in an array for each
 * type, in the order that they should appear. It is assumed that the length
 * of these arrays is the same as the number of instances those items should
 * show up at any given point. This function also returns the width of the path.
 *
 * @returns {Object}
 */
const getRoomPositions = (rooms, containerWidth) => {
	// Only happens during demo, where the upper floor is empty
	if (!Array.isArray(rooms) || !rooms.length) {
		return {
			pathWidth: 0,
			roomPositions: [],
		};
	}

	const roomProperties = _.map(rooms, (room, i) => getRoomProperties(rooms, i));

	const assetWidths = getAdjustedAssetWidths(containerWidth);
	const {lessonRoom} = assetWidths;
	const initialLeft = lessonRoom / 2;
	const roomDistance = lessonRoom / 2;
	const roomPositions = [];
	for (let i = 0; i < rooms.length; ++i) {
		const props = roomProperties[i];
		const {
			roomIndex,
			stairIndex,
			isNorth,
			roomType,
			isUpstairs,
			isStaircase,
		} = props;
		let left;
		if (isUpstairs) {
			// upstairs - need to make modifications to fit the roofline
			const prevPosition = _.last(roomPositions);
			const prevProps = roomProperties[i - 1];
			const isStartOfChunk = prevProps
				? prevProps.stairIndex !== stairIndex
				: false;
			// this could all technically be one equation... but this is easier to read
			left = isNorth ? 0 : roomDistance;
			if (prevPosition) {
				left = prevPosition.left + roomDistance;
				if (isStartOfChunk) {
					left += roomDistance;
					if (prevProps.isNorth === isNorth) {
						left += roomDistance;
					}
				}
			}
		} else {
			if (roomType === "fauxRoom") {
				left = initialLeft + roomDistance * (roomIndex - 1);
			} else {
				left = initialLeft + roomDistance * roomIndex;
			}
		}
		roomPositions.push({
			cardinal: isNorth ? "north" : "south",
			left,
			width: assetWidths[roomType],
			isFaux: roomType === "fauxRoom",
			isStaircase,
			isTest: roomType === "testRoom",
			isUpstairs,
		});
	}
	const {left, width, isUpstairs} = _.last(roomPositions);

	// add fake post-test room to support having the avatar after the last room
	const exitPadding = isUpstairs ? lessonRoom / 2 : 0;
	const pathWidth = left + width + exitPadding;
	return {
		pathWidth,
		roomPositions,
	};
};

const getRoomProperties = (rooms, index) => {
	const room = rooms[index];
	const isUpstairs = blockIsStaircase(room)
		? blockIsDownstairs(room[0])
		: blockIsUpstairs(room);
	let roomType = "lessonRoom";
	let roomIndex = 0;
	// Which set of stairs this room/stair belongs to
	let stairIndex = -1;
	let isNorth = false;
	let downstairsRooms = [];
	for (let i = 0; i < rooms.length; i++) {
		const r = rooms[i];
		if (blockIsStaircase(r)) {
			stairIndex++;
			if (isUpstairs) {
				downstairsRooms = downstairsRooms.concat(r);
				isNorth = downstairsRooms.length % 2 === 1;
			}
			if (index === i) break;
		} else {
			const prev = rooms[i - 1];
			// first rooms upstairs should be next to their corresponding staircase
			if (!(isUpstairs && blockIsStaircase(prev))) isNorth = !isNorth;
			if (index === i) break;
			roomIndex++;
		}
	}
	if (blockIsStaircase(room)) {
		roomType = "staircase";
	} else if (TrainerCore.isTestBlock(room)) {
		roomType = "testRoom";
	} else if (blockIsFaux(room)) {
		roomType = "fauxRoom";
	}
	return {
		isStaircase: blockIsStaircase(room),
		roomIndex,
		stairIndex,
		roomType,
		isUpstairs,
		isNorth,
	};
};

/**
 * Downstairs is all core blocks, with each "chunk" separated by stairs.
 * Upstairs is all bonus blocks, with each "chunk" separated by stairs.
 * Test block is added to the end of the downstairs blocks.
 * @param {[Object]} blockModels
 * @param {Object} auxBlocks
 * @param {Boolean} isUpstairs
 * @returns {{[Objects]}} list of rooms (i.e. rooms) and stairs (array of
 * blockModels) for upstairs and downstairs rooms
 */
const calculateRooms = (blockModels, auxBlocks) => {
	let upstairs = [];
	let downstairs = [];
	const blocksByChunk = TrainerCore.sortBlocksByChunk(blockModels);
	for (const chunk of blocksByChunk) {
		if (blockIsUpstairs(_.first(chunk))) {
			upstairs = upstairs.concat(chunk);
			downstairs.push(chunk);
		} else {
			downstairs = downstairs.concat(chunk);
			upstairs.push(chunk);
		}
	}
	if (auxBlocks.test) {
		const testModel = auxBlocks.test.model;
		const fauxModel = _.extend({}, testModel, {
			blockStyleType: "faux",
			blockType: "faux",
		});
		// add fake post-test room to support having avatar after the test room
		downstairs.push(testModel, fauxModel);
	}
	if (blockIsStaircase(_.last(upstairs))) {
		// Last item in upstairs shouldn't be stairs, so remove it
		upstairs = _.initial(upstairs);
	}
	return {
		downstairs,
		upstairs,
	};
};

const getPathWidth = (containerWidth, roomData) => {
	const {pathWidth} = getRoomPositions(roomData, containerWidth);
	return pathWidth;
};

const getAvatarPosition = (roomPosition, containerWidth, options = {}) => {
	const {isFaux, isStairs, isTest} = options;
	const {testRoom, fauxRoom, staircase, lessonRoom} = getAdjustedAssetWidths(
		containerWidth
	);
	let offset = 0;
	if (isTest) {
		offset = testRoom * 0.05;
	} else if (isFaux) {
		offset = fauxRoom * 0.05;
	} else if (isStairs) {
		offset = staircase * 0.25;
	} else {
		offset = lessonRoom * 0.125;
	}
	return roomPosition + offset;
};

/**
 * All readings without a corresponding lesson are core readings
 */
const getRequirementLevelForReading = (sectionID, sectionBlockMap) => {
	const sectionBlocks = sectionBlockMap[sectionID];
	return sectionBlocks.length > 0
		? _.first(sectionBlocks).requirementLevel
		: "core";
};

/**
 * Note that a building being unlocked is not exactly the same as a chapter being
 * unlocked. An unlocked building just means that at least one of the rooms is
 * unlocked, which allows for a lit-up hallway and for the avatar to show up in
 * the building, whereas an unlocked chapter must have the first lesson be unlocked.
 */
const buildingIsUnlocked = chapterModel =>
	chapterModel?.unlocked || chapterModel?.hasUnlockedBlocks;

const staircaseIsUnlocked = upstairsBlockModels => {
	return upstairsBlockModels.some(block => block.unlocked);
};

/**
 * A reading is considered unlocked if at least one of the following is true:
 *	1. an associated lesson is unlocked
 *	2. the preceding lesson is unlocked and completed
 * 		- for downstairs readings, this would be the preceding core lesson
 * 		- for upstairs readings, this would be the preceding enrichment lesson IN THE SAME ENRICHMENT CHUNK
 * 			- this can also be interpreted as the last lesson in the preceding book section (if it was an enrichment lesson)
 * 	3. the succeeding lesson is unlocked
 * 		- this is necessary for supplementary readings (readings with no associated lessons) in free-play, all of which are
 * 			downstairs, so this rule should specifically look for the succeeding core lesson
 *  4. if it's the first room of an unlocked chapter
 *  5. if the succeeding lesson is a test
 *    - unlock if the previous lesson is unlocked
 */
const readingIsUnlocked = (
	sectionID,
	sectionIDs,
	sectionBlockMap,
	testBlock,
	chapterIsUnlocked
) => {
	// check associated lessons
	const unlockedLessons = _.filter(
		_.flatten(_.values(sectionBlockMap)),
		block => block.bookSectionID === sectionID && block.unlocked
	);
	const hasUnlockedLesson = unlockedLessons.length > 0;

	const requirementLevel = getRequirementLevelForReading(
		sectionID,
		sectionBlockMap
	);
	const precedingBlock = getPrecedingBlock(
		sectionID,
		sectionIDs,
		sectionBlockMap
	);
	// NOTE: we don't need to look for succeeding blocks for upstairs readings
	const succeedingBlock = blockIsDownstairs(requirementLevel)
		? getSucceedingBlock(sectionID, sectionIDs, sectionBlockMap, testBlock)
		: null;

	const precedingBlockIsUnlocked = TrainerCore.blockIsUnlocked(precedingBlock);
	// a parent can lock an already-completed lesson
	const precedingBlockIsUnlockedAndCompleted = precedingBlock
		? precedingBlockIsUnlocked && blockIsComplete(precedingBlock)
		: false;
	const succeedingBlockIsUnlocked = TrainerCore.blockIsUnlocked(
		succeedingBlock
	);

	// if a core reading has no preceding block (first room), unlock with the chapter
	const unlockAsFirst =
		blockIsDownstairs(requirementLevel) && !precedingBlock
			? chapterIsUnlocked
			: false;

	// if a core reading is succeeded by the test, unlock with the previous room
	const unlockAsLast =
		TrainerCore.isCoreBlock(requirementLevel) &&
		succeedingBlock &&
		TrainerCore.isTestBlock(succeedingBlock)
			? precedingBlockIsUnlocked
			: false;

	return (
		hasUnlockedLesson ||
		precedingBlockIsUnlockedAndCompleted ||
		succeedingBlockIsUnlocked ||
		unlockAsFirst ||
		unlockAsLast
	);
};

/**
 * Retrieves the block that comes right before a reading along the chapter path
 * @param {*} sectionID
 * @param {*} sectionIDs
 * @param {*} sectionBlockMap
 * @returns {Object|null}
 */
const getPrecedingBlock = (sectionID, sectionIDs, sectionBlockMap) => {
	const requirementLevel = getRequirementLevelForReading(
		sectionID,
		sectionBlockMap
	);
	let precedingBlock = null;

	if (blockIsDownstairs(requirementLevel)) {
		// if downstairs, use last block of previous downstairs section
		const coreSectionIDs = _.filter(sectionIDs, id =>
			blockIsDownstairs(getRequirementLevelForReading(id, sectionBlockMap))
		);
		const previousSectionID = getPreviousSectionID(sectionID, coreSectionIDs);
		if (_.isNil(previousSectionID)) return null;
		const previousSectionBlocks = sectionBlockMap[previousSectionID];
		const coreBlocks = _.filter(previousSectionBlocks, block =>
			blockIsDownstairs(block)
		);

		if (coreBlocks.length === 0) {
			// if no core blocks, go back further
			precedingBlock = getPrecedingBlock(
				previousSectionID,
				sectionIDs,
				sectionBlockMap
			);
		} else {
			precedingBlock = _.last(coreBlocks);
		}
	} else if (blockIsUpstairs(requirementLevel)) {
		// if upstairs, use last block of previous section if it's also upstairs
		const previousSectionID = getPreviousSectionID(sectionID, sectionIDs);
		if (_.isNil(previousSectionID)) return null;

		const previousSectionBlocks = sectionBlockMap[previousSectionID];
		const potentialBlock = _.last(previousSectionBlocks);
		if (potentialBlock && blockIsUpstairs(potentialBlock)) {
			precedingBlock = potentialBlock;
		}
		// NOTE: We don't need to look for the succeeding block for upstairs readings because all
		// supplementary readings are downstairs.
	}

	return precedingBlock;
};

const getSucceedingBlock = (
	sectionID,
	sectionIDs,
	sectionBlockMap,
	testBlock
) => {
	let succeedingBlock = null;

	const requirementLevel = getRequirementLevelForReading(
		sectionID,
		sectionBlockMap
	);
	const sectionIndex = sectionIDs.indexOf(sectionID);
	if (blockIsDownstairs(requirementLevel)) {
		for (let i = sectionIndex; i < sectionIDs.length; ++i) {
			const id = sectionIDs[i];
			const sectionCoreBlocks = _.filter(sectionBlockMap[id], block =>
				blockIsDownstairs(block)
			);
			if (sectionCoreBlocks.length > 0) {
				succeedingBlock = _.first(sectionCoreBlocks);
				break;
			}
		}

		// if we couldn't find a succeeding core block, it must be right before the test block
		succeedingBlock = succeedingBlock || testBlock;
	} else if (blockIsUpstairs(requirementLevel)) {
		// NOTE: We don't need to look for the succeeding block for upstairs readings because all
		// supplementary readings are downstairs.
	}

	return succeedingBlock;
};

const getPreviousSectionID = (sectionID, sectionIDs) => {
	const sectionIndex = sectionIDs.indexOf(sectionID);
	const previousSectionID = sectionIDs[sectionIndex - 1] ?? null;
	return previousSectionID;
};

const roomIsComplete = (room, checkTrophy) => {
	if (checkTrophy) return room.isTrophyComplete;
	if (room.isReading) return room.isComplete;
	return blockIsComplete(room);
};

const blockIsComplete = block => block.starsObtained > 0;

const isSameRoom = (a, b) => {
	if (!_.isObject(a) || !_.isObject(b)) return false;

	const isSameReading =
		a.isReading &&
		b.isReading &&
		!_.isNil(a.bookSectionID) &&
		!_.isNil(b.bookSectionID) &&
		a.bookSectionID === b.bookSectionID;
	const isSameLesson =
		!a.isReading &&
		!b.isReading &&
		!_.isNil(a.blockID) &&
		!_.isNil(b.blockID) &&
		a.blockID === b.blockID &&
		// only compare block types if both room objects have it
		(!a.blockType || !b.blockType || a.blockType === b.blockType);
	return isSameReading || isSameLesson;
};

/**
 * Returns the corresponding chunk of blocks that go with a staircase
 * i.e. If upstairs stairs, returns the upstairs blocks in that hallway.
 * i.e. If downstairs stairs, returns the downstairs blocks in that chunk
 * leading up to the stairs.
 *
 * @param {[Object]} blocksOnFloor All blocks on the given floor
 * @param {Integer} i Index of the stair, in relation to all rooms
 * @param {Boolean} isUpstairs Whether this staircase is upstairs
 *
 * @return {Array} Array of blocks corresponding to stair
 */
const getBlocksForStairs = (blocksOnFloor, i, isUpstairs) => {
	let correspBlocks;
	if (isUpstairs) {
		let nextStairsIndex = _.findIndex(
			blocksOnFloor,
			(d, j) => blockIsStaircase(d) && j > i
		);
		// If no stairs after this one, then go to last room
		if (nextStairsIndex < 0) nextStairsIndex = blocksOnFloor.length;
		correspBlocks = _.slice(blocksOnFloor, i + 1, nextStairsIndex);
	} else {
		let previousStairsIndex = _.findLastIndex(
			blocksOnFloor,
			(d, j) => blockIsStaircase(d) && j < i
		);
		// If no stairs after this one, then go to last room
		if (previousStairsIndex < 0) previousStairsIndex = -1;
		correspBlocks = _.slice(blocksOnFloor, previousStairsIndex + 1, i);
	}
	return correspBlocks;
};

/**
 * Returns the block chunk that an upstairs block is in. Note that
 * this assumes that the blocksOnFloor array is an upstairs view.
 * @param {[Objects]} blocksOnFloor
 * @param {Integer} i
 * @returns {[Objects]}
 */
const getChunkForBlock = (blocksOnFloor, i) => {
	const stairsIndex = _.findLastIndex(
		blocksOnFloor,
		(d, j) => blockIsStaircase(d) && j < i
	);
	return getBlocksForStairs(blocksOnFloor, stairsIndex, true);
};

/**
 * Just a helper to not have to keep converting a boolean isUpstairs
 * to strings "upstairs" or "downstairs"
 * @param {Boolean} isUpstairs
 * @returns {String}
 */
const getFloorStr = isUpstairs => (isUpstairs ? "upstairs" : "downstairs");

/**
 * @param {Object} allRooms {downstairs: [], upstairs: []}
 * @param {Boolean|String} isUpstairs
 * @returns {Array}
 */
const getRoomsForFloor = (allRooms, isUpstairs) =>
	allRooms[getFloorStr(isUpstairs)] || [];

/**
 * Returns the roomIndex that corresponds to a certain stairIndex.
 * Note that this function is floor-agnostic.
 * @param {Array} rooms Array of rooms for a certain floor
 * @param {Number} stairIndex
 */
const getRoomIndexForStairIndex = (rooms, stairIndex) => {
	const stairs = _.filter(rooms, r => blockIsStaircase(r));
	const roomIndex = _.findIndex(rooms, r => _.isEqual(r, stairs[stairIndex]));
	return roomIndex;
};

/**
 * Calculate new left position on floor after taking the stairs from
 * one floor to another
 * @param {Number} width width of container
 * @param {[Object]} rooms all room data
 * @param {Boolean} newIsUpstairs new floor is upstairs
 * @param {Integer} stairIndex index of staircase out of all the stairs
 * @param {Number} currX left position on current floor
 * @returns {Number} left position
 */
const calculateNewFloorPosition = (
	width,
	rooms,
	newIsUpstairs,
	stairIndex,
	currX
) => {
	const {lessonRoom, hallSectionEntry} = getAdjustedAssetWidths(width);
	const floorStr = getFloorStr(newIsUpstairs);
	const roomData = rooms[floorStr];
	// Get corresponding staircase on other level
	const roomIndex = getRoomIndexForStairIndex(roomData, stairIndex);
	const {roomPositions} = getRoomPositions(roomData, width);
	// Stair position on the new floor
	const stairPosition = roomPositions[roomIndex];
	let stairX = stairPosition.left;
	if (newIsUpstairs) {
		// For upstairs, it should take you up the staircase with the hallway
		// corresponding to the stairs centered on the page, if possible.
		const correspBlocks = getBlocksForStairs(
			roomData,
			roomIndex,
			newIsUpstairs
		);
		const hallSize = correspBlocks.length * (lessonRoom / 2) + hallSectionEntry;
		if (hallSize < width) stairX = stairX - (width - hallSize) / 2;
	} else {
		// For downstairs, it should take you down the staircase with the
		// staircase at the same location it was at upstairs, if possible.

		// Get staircase position at current level
		const oldLevelStr = newIsUpstairs ? "downstairs" : "upstairs";
		const oldRoomData = rooms[oldLevelStr];
		const oldStairs = _.filter(oldRoomData, r => blockIsStaircase(r));
		const oldRoomIndex = _.findIndex(oldRoomData, r =>
			_.isEqual(r, oldStairs[stairIndex])
		);
		// Stair position on the new floor
		const {roomPositions: oldRoomPositions} = getRoomPositions(
			oldRoomData,
			width
		);
		const oldStairPosition = oldRoomPositions[oldRoomIndex];
		const oldStairX = oldStairPosition.left;
		const diff = oldStairX - currX;
		stairX = stairX - diff;
	}
	return stairX;
};

const getAnimationTriggerData = (chapterPath, page) => {
	const roomsDownstairs = chapterPath?.rooms?.downstairs;
	if (!_.isArray(roomsDownstairs) || roomsDownstairs.length === 0) {
		return null;
	}
	const demo = page?.demo;
	const pageName = page?.name;
	const otherFloor = floor =>
		floor === "upstairs" ? "downstairs" : "upstairs";
	const getRoomInfo = (room, floor) => {
		let type = null;
		let rooms;
		let latestStars;
		if (blockIsStaircase(room)) {
			type = "staircase";
			rooms = room.map(rm => getRoomInfo(rm, otherFloor(floor)));
		} else if (room.isReading) {
			type = "reading";
		} else if (room.blockType === "test") {
			type = "test";
			if (demo && !room.recentHistory) {
				latestStars = 0;
			} else {
				latestStars = room.recentHistory.length
					? _.last(room.recentHistory).stars
					: 0;
			}
		} else {
			type = "lesson";
			if (demo && !room.recentHistory) {
				latestStars = 0;
			} else {
				latestStars = room.recentHistory.length
					? _.last(room.recentHistory).stars
					: 0;
			}
		}
		const {
			blockID,
			blockIndex,
			bookSectionID,
			chapterIndex,
			displayName, // useful for debugging
			gradeNumber,
			isComplete,
			isTrophyAvailable,
			isTrophyComplete,
			starsObtained,
			timesCompleted,
			unlocked,
		} = room;
		return {
			type,
			blockID,
			blockIndex,
			bookSectionID,
			chapterIndex,
			displayName,
			floor,
			gradeNumber,
			isComplete,
			isTrophyAvailable,
			isTrophyComplete,
			latestStars,
			rooms,
			starsObtained,
			timesCompleted,
			unlocked: type === "staircase" ? staircaseIsUnlocked(rooms) : unlocked,
		};
	};
	const roomsByFloor = _.mapValues(chapterPath.rooms, (roomsOnFloor, floor) =>
		roomsOnFloor.map(room => getRoomInfo(room, floor))
	);
	const rooms = [...roomsByFloor.downstairs, ...roomsByFloor.upstairs];
	let inRoom = null;
	if (page.showBook && page.showBookSectionID) {
		inRoom = {
			type: "reading",
			bookSectionID: page.showBookSectionID,
		};
	} else {
		inRoom = {
			type: OpsPage.isPageForTestBlock(page) ? "test" : "lesson",
			blockIndex: page.blockIndex, // null for tests
			chapterIndex: page.chapterIndex, // null for tests
			gradeNumber: page.gradeNumber, // null for tests
		};
	}
	return {
		chapterIndex: page.chapterIndex,
		gradeNumber: page.gradeNumber,
		pageName,
		inRoom,
		rooms,
	};
};

const computeVaultAnimationTriggers = (animateFrom, animateStep, animateTo) => {
	if (!animateFrom || !animateStep || !animateTo) {
		return [];
	}
	const diffFrom = (path, f) =>
		f(_.get(animateFrom, path), _.get(animateTo, path));
	const diffStep = (path, f) =>
		f(_.get(animateStep, path), _.get(animateTo, path));
	const neq = (a, b) => a !== b;
	const animationTriggers = [];
	const {inRoom} = animateStep;
	try {
		if (
			inRoom &&
			inRoom.type === "reading" &&
			diffStep("inRoom.bookSectionID", neq) &&
			animateTo.pageName === "trainer.chapter" &&
			animateStep.pageName === "trainer.chapter" &&
			animateFrom.pageName === "trainer.chapter"
		) {
			// check for leaving reading rooms
			const roomFrom = _.find(animateFrom.rooms, inRoom);
			const roomStep = _.find(animateStep.rooms, inRoom);
			const roomTo = _.find(animateTo.rooms, inRoom);
			const earnedCompletion =
				roomTo.isComplete && !(roomFrom || roomStep).isComplete;
			const isComplete = roomTo.isComplete;
			animationTriggers.push({
				type: "left-reading-room",
				room: inRoom,
				earnedCompletion,
				isComplete,
				roomFrom,
				roomStep,
				roomTo,
			});
		}
		const leftProblemPage = diffStep(
			"pageName",
			(was, is) => was === "trainer.problem" && is === "trainer.chapter"
		);
		if (leftProblemPage) {
			// check for leaving lesson rooms
			const roomFrom = _.find(animateFrom.rooms, inRoom);
			const roomTo = _.find(animateTo.rooms, inRoom);
			const earnedStars =
				roomTo.starsObtained > roomFrom.starsObtained
					? roomTo.starsObtained
					: 0;
			const unlockedTrophy =
				roomTo.isTrophyAvailable && !roomFrom.isTrophyAvailable;
			const earnedTrophy =
				roomTo.isTrophyComplete && !roomFrom.isTrophyComplete;
			const gotAtLeast1Star =
				roomTo.timesCompleted > roomFrom.timesCompleted && !!roomTo.latestStars;
			const leftEarly = roomTo.timesCompleted === roomFrom.timesCompleted;
			const roomsUnlocked = animateTo.rooms.filter(
				(rm, i) => rm.unlocked && !animateFrom.rooms[i].unlocked
			);
			const prevStars = roomFrom.starsObtained;
			const wasComplete = !!roomFrom.isComplete;
			const trigger = {
				type: "left-lesson-room",
				room: inRoom,
				earnedStars,
				unlockedTrophy,
				earnedTrophy,
				gotAtLeast1Star,
				leftEarly,
				roomsUnlocked,
				prevStars,
				wasComplete,
			};
			animationTriggers.push(trigger);
		}
	} catch (e) {
		const attrs = [
			"blockIndex",
			"bookSectionID",
			"chapterIndex",
			"gradeNumber",
			"type",
		];
		const trimRooms = rooms => _.map(rooms, room => _.pick(room, attrs));
		const trimmedRoomsFrom = trimRooms(animateFrom.rooms);
		const trimmedRoomsStep = trimRooms(animateStep.rooms);
		const trimmedRoomsTo = trimRooms(animateTo.rooms);
		/**
		 * UPDATE: This *should* be fixed in https://github.com/aops-ba/ba/pull/4950,
		 * but I'm going to leave this in here in case there are other issues.
		 *
		 * Sometimes, at least one of roomFrom, roomStep, or roomTo is undefined. It
		 * probably has something to do with either a bad inRoom or a bad
		 * animationXX.rooms, but we're not sure which or why, so let's gather some logs.
		 */
		ErrorUtil.log(e.name, e.message, {
			inRoom,
			trimmedRoomsFrom_length: trimmedRoomsFrom?.length,
			trimmedRoomsStep_length: trimmedRoomsStep?.length,
			trimmedRoomsTo_length: trimmedRoomsTo?.length,
			trimmedRoomsFrom,
			trimmedRoomsStep,
			trimmedRoomsTo,
		});
	}
	if (
		diffFrom("gradeNumber", neq) ||
		diffFrom("chapterIndex", neq) ||
		// When the rooms change (which is async after the chapter changes),
		// the animateFrom is updated. But the above conditions alone would
		// update animateFrom to have the right number but wrong room data,
		// so this condition makes sure the room data updates when available
		// as well.
		!_.isEqual(
			animateFrom.rooms.map(rm => rm.blockID),
			animateTo.rooms.map(rm => rm.blockID)
		)
	) {
		animationTriggers.push({
			type: "entered-chapter",
		});
	}
	return animationTriggers;
};

const computeStateAnimationTriggers = view => {
	const {state, justSwitchedFloors} = view;
	const animationTriggers = [];
	if (state.scrollToAvatar) {
		animationTriggers.push({
			type: "find-my-avatar",
		});
	}
	if (justSwitchedFloors) {
		animationTriggers.push({
			type: "used-stairs",
		});
	}
	if (state.idleAnimate) {
		animationTriggers.push({
			type: "idle-avatar",
		});
	}
	return animationTriggers;
};

const canAnimate = vaultState => {
	return (
		vaultState.page.name === "trainer.chapter" &&
		!vaultState.page.showBook &&
		!vaultState.page.isShowingNotification
	);
};

const expandAnimations = animationTriggers => {
	return _.flatten(
		animationTriggers.map(trigger => {
			if (
				trigger.type === "left-reading-room" ||
				trigger.type === "left-lesson-room"
			) {
				if (
					trigger.earnedCompletion ||
					trigger.earnedStars ||
					trigger.earnedTrophy
				) {
					return [{type: "earned-something", ..._.omit(trigger, "type")}];
				} else {
					return [{type: "look-at-me"}];
				}
			}
			if (
				trigger.type === "entered-chapter" ||
				trigger.type === "used-stairs"
			) {
				return [{type: "look-at-me"}];
			}
			if (trigger.type === "find-my-avatar") {
				return [{type: "look-at-me"}];
			}
			if (trigger.type === "idle-avatar") {
				return [{type: "idle-bounce"}];
			}
		})
	);
};

const expandSplashes = animationTriggers => {
	return _.flatten(
		animationTriggers.map(trigger => {
			if (
				trigger.type === "entered-chapter" ||
				trigger.type === "used-stairs"
			) {
				return [{type: "loading-screen"}];
			}
			return [];
		})
	);
};

module.exports = {
	getAdjustedAssetWidths,
	getBackgroundAssets,
	getStaircaseAsset,
	getDottedPathAsset,
	getRoomAsset,
	getPathWidth,
	calculateRooms,
	blockIsFaux,
	blockIsStaircase,
	blockIsUpstairs,
	getRoomPositions,
	getAvatarPosition,
	getRequirementLevelForReading,
	buildingIsUnlocked,
	staircaseIsUnlocked,
	readingIsUnlocked,
	getPrecedingBlock,
	getSucceedingBlock,
	roomIsComplete,
	blockIsComplete,
	isSameRoom,
	getBlocksForStairs,
	getChunkForBlock,
	getFloorStr,
	getRoomsForFloor,
	getRoomIndexForStairIndex,
	calculateNewFloorPosition,
	getAnimationTriggerData,
	computeVaultAnimationTriggers,
	computeStateAnimationTriggers,
	canAnimate,
	expandAnimations,
	expandSplashes,
};
